import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import { AgreementDetailed } from "./agreementDetailed"
import { IAgreementDetailed } from "./agreementDetailed.types"
import { Tariff } from "../tariff/tariff"
import { IUnitRates } from "../tariff/tariff.types"
import { MeterPointAgreementRaw } from "../meterpointAgreement/meterpointAgreement.parser"

// Junifer's sentinel for an open-ended agreement. The legacy Agreement model
// normalises it to a null endDate, so a missing backoffice toDate maps onto
// the same open-ended semantics the Ark endpoint had.
const OPEN_ENDED_TO_DATE = "9999-12-31"

const capitalise = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1)

const UNIT_RATE_NAMES = ["Standard", "Day", "Night"] as const

const parseUnitRates = (
  tariffRates: MeterPointAgreementRaw["productDetails"]["tariffRates"],
): IUnitRates | undefined => {
  const rates = tariffRates
    ?.map((rate) => [capitalise(rate?.name ?? ""), rate?.rateInPence] as const)
    .filter(
      (entry): entry is [(typeof UNIT_RATE_NAMES)[number], number] =>
        UNIT_RATE_NAMES.includes(
          entry[0] as (typeof UNIT_RATE_NAMES)[number],
        ) && entry[1] != null,
    )
  if (!rates?.length) return undefined

  return Object.fromEntries(rates) as IUnitRates
}

export const BackofficeAgreementParser: IParser<
  IAgreementDetailed,
  MeterPointAgreementRaw
> = {
  toDomain: (data: MeterPointAgreementRaw) => {
    try {
      const productDetails = data.productDetails
      const isGas = productDetails.fuelType === "GAS"
      const unitRates = parseUnitRates(productDetails.tariffRates)

      return new AgreementDetailed({
        id: Number(data.id),
        startDate: data.fromDate,
        endDate: data.toDate ?? OPEN_ENDED_TO_DATE,
        orderedDate: data.orderedDate ?? null,
        isCancelled: data.isCancelled,
        productName: productDetails.displayName,
        productCode: productDetails.code,
        juniferNumber: data.juniferAgreementNumber,
        tariff: new Tariff({
          id: Number(productDetails.id),
          code: productDetails.code,
          gsp: productDetails.gspGroup,
          display: productDetails.displayName,
          productDetails: {
            name: productDetails.internalName,
            // The backoffice response has no tariff effective-from date; the
            // agreement start is the closest equivalent and nothing in the
            // agreement consumers reads it.
            fromDttm: dayjs(data.fromDate),
            unitRate: isGas ? unitRates?.Standard : undefined,
            unitRates: isGas ? undefined : unitRates,
            standingCharge: productDetails.standingChargeInPence,
            earlyTerminationCharge:
              productDetails.earlyTerminationChargeInPounds,
            // Junifer tariff-comparison metadata is not exposed by the
            // backoffice agreements endpoint and is unused by agreement
            // consumers.
            tariffComparisonCode: "",
            tariffComparisonRateName: "",
          },
          isPrepay: productDetails.isPrepay,
          isSmartPrepay: productDetails.isSmartPrepay,
          isAvailableToPublic: productDetails.isAvailableToPublic,
          directDebit: productDetails.requiresDirectDebit,
          durationMonths:
            productDetails.durationInMonths != null
              ? Number(productDetails.durationInMonths)
              : null,
          createdAt: dayjs(data.fromDate),
        }),
      })
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
